import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { Constants } from '@app/common/constants';
import { HttpHelper } from '@app/common/http-helper';
import { RestaurantMenu } from '@app/models/restaurant-menu';
import { CacheService } from '@app/services/cache.service';
import { Environment } from '@environments/interface.environment';
import { endOfISOWeek, getISOWeek, getISOWeekYear } from 'date-fns';
import { BehaviorSubject, concat, Observable, of, Subject } from 'rxjs';
import { catchError, map, shareReplay, takeUntil, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class RestaurantMenuService {

  constructor(@Inject(CUSTOMERENVIRONMENT) private environment: Environment,
              private http: HttpClient,
              private cache: CacheService,
              private httpHelper: HttpHelper) { }

  private readonly cacheKey = 'RestaurantMenuService-getAllRestaurantMenus';
  private readonly cacheKeyPrefix = 'RestaurantMenuService-getCurrentRestaurantMenusForRestaurant';
  public useCache = true;
  private internalReload$ = new Subject<void>();
  public reload$ = new BehaviorSubject<void>(null);
  private getAllRestaurantMenus$: Observable<RestaurantMenu[]>;
  private getCurrentRestaurantMenusForRestaurant$ = new Map<string, Observable<RestaurantMenu[]>>();

  public reload() {
    this.getAllRestaurantMenus$ = null;
    this.getCurrentRestaurantMenusForRestaurant$.clear();
    if (this.useCache) {
      this.cache.removeAllItemsInPersistentCacheWithGeneratedKeyPrefix(this.cacheKey);
      this.cache.removeAllItemsInPersistentCacheWithGeneratedKeyPrefix(this.cacheKeyPrefix);
    }
    this.internalReload$.next();
    this.reload$.next(null);
  }

  getCurrentRestaurantMenusForRestaurant(restaurantId: number): Observable<Array<RestaurantMenu>> {
    const currentWeekNumber = getISOWeek(new Date()).toString();
    const currentWeekYear = getISOWeekYear(new Date()).toString();

    const cacheKey = `${this.cacheKeyPrefix}-${restaurantId}-${currentWeekNumber}-${currentWeekYear}`;

    if (this.getCurrentRestaurantMenusForRestaurant$.has(cacheKey)) {
      return this.getCurrentRestaurantMenusForRestaurant$.get(cacheKey);
    } else {
      const httpParams = new HttpParams()
        .append('weekNumber', currentWeekNumber)
        .append('year', currentWeekYear)
        .append('restaurantId', restaurantId.toString());

      this.getCurrentRestaurantMenusForRestaurant$.set(cacheKey, this.http
        .get<Array<RestaurantMenu>>(`${Constants.API_ENDPOINT}/restaurantmenus`, {
          params: httpParams,
        })
        .pipe(
          tap(data => {
            this.useCache && this.cache.set(cacheKey, data, endOfISOWeek(new Date()));
          }),
          catchError(this.httpHelper.handleError({ operation: 'getCurrentRestaurantMenusForRestaurant', result: new Array<RestaurantMenu>(), rethrow: true, cacheKey: cacheKey })),
          takeUntil(this.internalReload$),
          shareReplay({ bufferSize: 1, refCount: true })
        ));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<RestaurantMenu>>(cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getCurrentRestaurantMenusForRestaurant$.get(cacheKey));
        }
      }
      return this.getCurrentRestaurantMenusForRestaurant$.get(cacheKey);
    }
  }

  getCurrentAndFutureRestaurantMenusForRestaurant(restaurantId: number): Observable<Array<RestaurantMenu>> {
    const currentWeekNumber = getISOWeek(new Date());
    const nextWeekNumber = getISOWeek(new Date()) + 1;
    const nextSecondWeekNumber = getISOWeek(new Date()) + 2;
    const currentWeekYear = getISOWeekYear(new Date()).toString();

    const cacheKey = `${this.cacheKeyPrefix}-${restaurantId}-${currentWeekNumber + ',' + nextWeekNumber + ',' + nextSecondWeekNumber}-${currentWeekYear}`;

    if (this.getCurrentRestaurantMenusForRestaurant$.has(cacheKey)) {
      return this.getCurrentRestaurantMenusForRestaurant$.get(cacheKey);
    } else {
      const httpParams = new HttpParams()
        .append('weekNumber', currentWeekNumber + ',' + nextWeekNumber + ',' + nextSecondWeekNumber)
        .append('year', currentWeekYear)
        .append('restaurantId', restaurantId.toString());

      this.getCurrentRestaurantMenusForRestaurant$.set(cacheKey, this.http
        .get<Array<RestaurantMenu>>(`${Constants.API_ENDPOINT}/restaurantmenus/weeks`, {
          params: httpParams,
        })
        .pipe(
          tap(data => {
            this.useCache && this.cache.set(cacheKey, data, endOfISOWeek(new Date()));
          }),
          catchError(this.httpHelper.handleError({ operation: 'getCurrentRestaurantMenusForRestaurant', result: new Array<RestaurantMenu>(), rethrow: true, cacheKey: cacheKey })),
          takeUntil(this.internalReload$),
          shareReplay({ bufferSize: 1, refCount: true })
        ));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<RestaurantMenu>>(cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getCurrentRestaurantMenusForRestaurant$.get(cacheKey));
        }
      }
      return this.getCurrentRestaurantMenusForRestaurant$.get(cacheKey);
    }
  }

  getAllRestaurantMenus(): Observable<Array<RestaurantMenu>> {
    if (this.getAllRestaurantMenus$) {
      return this.getAllRestaurantMenus$;
    } else {
      this.getAllRestaurantMenus$ = this.http
        .get<Array<RestaurantMenu>>(`${Constants.API_ENDPOINT}/restaurantmenus/all`)
        .pipe(
          tap(data => {
            this.useCache && this.cache.set(this.cacheKey, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getAllRestaurantMenus', result: new Array<RestaurantMenu>(), rethrow: true, cacheKey: this.cacheKey })),
          takeUntil(this.internalReload$),
          shareReplay({ bufferSize: 1, refCount: true }));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<RestaurantMenu>>(this.cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getAllRestaurantMenus$);
        }
      }
      return this.getAllRestaurantMenus$;
    }
  }

  addRestaurantMenu(restaurantMenu: RestaurantMenu): Observable<RestaurantMenu> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    return this.http
      .post<RestaurantMenu>(`${Constants.API_ENDPOINT}/restaurantmenus`, restaurantMenu, { params: httpParams })
      .pipe(
        tap(() => {
          this.reload();
        }),
        catchError(this.httpHelper.handleError({ operation: 'addRestaurantMenu', result: null, rethrow: true })));
  }

  updateRestaurantMenu(id: number, restaurantMenu: RestaurantMenu): Observable<RestaurantMenu> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    return this.http
      .put<RestaurantMenu>(`${Constants.API_ENDPOINT}/restaurantmenus/${id}`, restaurantMenu, { params: httpParams })
      .pipe(
        tap(() => {
          this.reload();
        }),
        catchError(this.httpHelper.handleError({ operation: 'updateRestaurantMenu', result: null, rethrow: true })));
  }

  deleteRestaurantMenu(id: number): Observable<RestaurantMenu> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    const fetchOperation = this.http.delete<RestaurantMenu>(`${Constants.API_ENDPOINT}/restaurantmenus/${id}`, {
      params: httpParams
    })
      .pipe(
        map(response => {
          console.log(`deleted result: `);
          console.log(response);
          this.reload();
          return response;
        }),
        catchError(this.httpHelper.handleError({ operation: 'deleteRestaurantMenu', result: ({} as RestaurantMenu) })));

    return fetchOperation;
  }

  getRestaurantMenu(id: number): Observable<RestaurantMenu> {
    return this.http
      .get<RestaurantMenu>(`${Constants.API_ENDPOINT}/restaurantmenus/${id}`)
      .pipe(catchError(this.httpHelper.handleError({ operation: 'getRestaurantMenu', result: null, rethrow: true })));
  }

}
